export const MIN_BROWSER_VERSIONS = {
  chrome: 111,
  edge: 111,
  firefox: 128,
  safari: 16.4,
} as const;

export type BrowserName = keyof typeof MIN_BROWSER_VERSIONS;

export type DetectedBrowser =
  | { name: BrowserName; majorVersion: number; isOutdated: boolean; isIos: boolean }
  | { name: 'unknown'; majorVersion: null; isOutdated: false; isIos: false };

function match(ua: string, regex: RegExp): number | null {
  const m = ua.match(regex);
  if (!m) return null;
  const parsed = parseFloat(m[1]);
  return Number.isFinite(parsed) ? parsed : null;
}

export function detectBrowser(userAgent: string): DetectedBrowser {
  const ua = userAgent || '';

  // All iOS browsers use WebKit, so the WebKit version (Version/<x.y>) is what
  // gates feature support — the OS upgrade is what users actually need.
  if (/iPhone|iPad|iPod/.test(ua)) {
    const v = match(ua, /Version\/(\d+\.\d+)/);
    if (v === null) {
      return { name: 'unknown', majorVersion: null, isOutdated: false, isIos: false };
    }
    const isOutdated = v < MIN_BROWSER_VERSIONS.safari;
    let name: BrowserName = 'safari';
    if (ua.includes('CriOS/')) name = 'chrome';
    else if (ua.includes('FxiOS/')) name = 'firefox';
    return { name, majorVersion: v, isOutdated, isIos: true };
  }

  // Order matters: Edge UA contains "Chrome", Chrome UA contains "Safari".
  const edge = match(ua, /Edg\/(\d+)/);
  if (edge !== null) {
    return {
      name: 'edge',
      majorVersion: edge,
      isOutdated: edge < MIN_BROWSER_VERSIONS.edge,
      isIos: false,
    };
  }

  const firefox = match(ua, /Firefox\/(\d+)/);
  if (firefox !== null) {
    return {
      name: 'firefox',
      majorVersion: firefox,
      isOutdated: firefox < MIN_BROWSER_VERSIONS.firefox,
      isIos: false,
    };
  }

  const chrome = match(ua, /Chrome\/(\d+)/);
  if (chrome !== null) {
    return {
      name: 'chrome',
      majorVersion: chrome,
      isOutdated: chrome < MIN_BROWSER_VERSIONS.chrome,
      isIos: false,
    };
  }

  if (ua.includes('Safari/')) {
    const v = match(ua, /Version\/(\d+\.\d+)/);
    if (v !== null) {
      return {
        name: 'safari',
        majorVersion: v,
        isOutdated: v < MIN_BROWSER_VERSIONS.safari,
        isIos: false,
      };
    }
  }

  return { name: 'unknown', majorVersion: null, isOutdated: false, isIos: false };
}
